import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

/**
 * FinanceSummary
 *
 * Lightweight financial overview:
 * cost per acre, revenue per ton, estimated ROI, breakeven timeline.
 * Deliberately high-level — not detailed P&L.
 */

const FinanceSummary = ({ finance, isArabic }) => {
  const [ref, inView] = useInView({ triggerOnce: true, rootMargin: "-40px" });

  if (!finance) return null;

  const cards = [
    {
      icon: "💰",
      label: isArabic ? "تكلفة / فدان" : "Cost / Acre",
      value: finance.costPerAcre || "—",
      trend: finance.costTrend,
    },
    {
      icon: "📈",
      label: isArabic ? "عائد / طن" : "Revenue / Ton",
      value: finance.revenuePerTon || "—",
      trend: finance.revenueTrend,
    },
    {
      icon: "🔄",
      label: isArabic ? "العائد على الاستثمار" : "Est. ROI",
      value: finance.roi || "—",
      trend: null,
    },
    {
      icon: "📅",
      label: isArabic ? "نقطة التعادل" : "Breakeven",
      value: finance.breakeven || "—",
      trend: null,
    },
  ];

  return (
    <div ref={ref} className="rounded-2xl border border-slate-200 bg-white p-5 md:p-6 shadow-sm">
      <div className={`flex items-center gap-2 mb-5 ${isArabic ? "flex-row-reverse" : ""}`}>
        <span className="text-lg">💵</span>
        <h3 className="text-base font-bold text-slate-900 mb-0">
          {isArabic ? "نظرة مالية" : "Financial Summary"}
        </h3>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {cards.map((card, i) => (
          <motion.div
            key={card.label}
            className="rounded-xl bg-gradient-to-b from-slate-50 to-white border border-slate-100 p-4 text-center"
            initial={{ opacity: 0, y: 12 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: i * 0.1, duration: 0.5 }}
          >
            <span className="text-2xl">{card.icon}</span>
            <p className="text-lg font-bold text-slate-900 mt-2 mb-1">{card.value}</p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0">{card.label}</p>
            {card.trend && (
              <p className={`text-[10px] font-bold mt-1 mb-0 ${card.trend.startsWith("+") || card.trend.startsWith("↓") ? "text-emerald-600" : "text-red-500"}`}>
                {card.trend}
              </p>
            )}
          </motion.div>
        ))}
      </div>

      {/* Disclaimer */}
      <p className={`text-[10px] text-slate-400 mt-4 mb-0 ${isArabic ? "text-right" : ""}`}>
        {isArabic
          ? "* الأرقام المالية تقديرية بناءً على بيانات تشغيلية. لا تمثل بيانات مالية مدققة."
          : "* Financial figures are estimates based on operational data. They do not represent audited financials."}
      </p>
    </div>
  );
};

export default FinanceSummary;
